import axios from 'axios';
import { createCloudRunAxios } from './axios';

const remiApi = createCloudRunAxios('remi-api');

// Response types

interface CINCAuthorizationResponse {
  authorization_url: string;
  state: string;
  redirect_uri: string;
}

interface CINCStatusResponse {
  integration: {
    status: string;
    enabled: boolean;
    lastSyncAt: string;
    stats: {
      totalContactsSynced: number;
    };
  };
}

/**
 * Get CINC authorization URL to start the OAuth flow
 */
export const getCINCAuthorizationUrl = async (
  orgId: string
): Promise<CINCAuthorizationResponse> => {
  const response = await remiApi.get<CINCAuthorizationResponse>(
    '/api/v1/oauth/cinc/authorize',
    { params: { organization_id: orgId } }
  );
  return response.data;
};

/**
 * Check CINC integration status
 * Returns the status of the CINC integration for the organization
 */
export const getCINCStatus = async (orgId: string): Promise<CINCStatusResponse | null> => {
  try {
    const response = await remiApi.get<CINCStatusResponse>(
      '/api/v1/integrations/cinc/status',
      { params: { organization_id: orgId } }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      // Not connected
      return null;
    }
    throw error;
  }
};

/**
 * Open CINC OAuth popup and handle the authorization flow
 */
export const connectCINC = async (orgId: string): Promise<string> => {
  // Get the authorization URL
  const { authorization_url } = await getCINCAuthorizationUrl(orgId);

  // Open popup
  const popup = window.open(
    authorization_url,
    'CINC Authorization',
    'width=600,height=700,left=200,top=100'
  );

  if (!popup) {
    throw new Error('Popup blocked. Please allow popups for this site.');
  }

  // Listen for success/error messages from the OAuth callback (hosted on remi-api)
  return new Promise((resolve, reject) => {
    const messageHandler = (event: MessageEvent) => {
      // Validate origin — accept messages from remi-api or legacy cinc-connector
      // TODO: remove speculo-api-cinc-connector origin once the legacy connector is deprecated
      if (
        !event.origin.includes('remi-api') &&
        !event.origin.includes('speculo-api-cinc-connector')
      ) {
        return;
      }

      if (event.data.type === 'CINC_OAUTH_SUCCESS') {
        window.removeEventListener('message', messageHandler);
        popup.close();
        resolve(event.data.orgId);
      } else if (event.data.type === 'CINC_OAUTH_ERROR') {
        window.removeEventListener('message', messageHandler);
        popup.close();
        reject(new Error(event.data.description || 'Authorization failed'));
      }
    };

    window.addEventListener('message', messageHandler);

    // Handle popup closed without authorization
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        window.removeEventListener('message', messageHandler);
        reject(new Error('Authorization cancelled by user'));
      }
    }, 1000);
  });
};
